/** Utilitare de formatare — RON, date în română, countdown-uri. */

/** Formatează bani (în bani/cenți) ca RON, ex. 14900 → „149 lei". */
export function formatRon(cents: number, opts: { withDecimals?: boolean } = {}): string {
  const value = cents / 100;
  const hasFraction = value % 1 !== 0;
  const decimals = opts.withDecimals || hasFraction ? 2 : 0;
  return (
    new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value) + ' lei'
  );
}

/** Numere cu separator de mii în stil românesc. */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat('ro-RO').format(n);
}

const DATE_FMT = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
const DATE_SHORT = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long' });
const DATETIME_FMT = new Intl.DateTimeFormat('ro-RO', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return DATE_FMT.format(d);
}

export function formatDateShort(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return DATE_SHORT.format(d);
}

export function formatDateTime(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return DATETIME_FMT.format(d);
}

/** „acum 3 minute", „acum 2 ore", „ieri", data pentru mai vechi. */
export function timeAgo(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'chiar acum';
  if (min < 60) return `acum ${min} ${min === 1 ? 'minut' : 'minute'}`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `acum ${hours} ${hours === 1 ? 'oră' : 'ore'}`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'ieri';
  if (days < 7) return `acum ${days} zile`;
  return formatDateShort(d);
}

/** Countdown din secunde → „2z 04:12:33" sau „04:12:33" sau „12:33". */
export function formatCountdown(totalSeconds: number): string {
  if (totalSeconds <= 0) return '00:00';
  const s = Math.floor(totalSeconds % 60);
  const m = Math.floor((totalSeconds / 60) % 60);
  const h = Math.floor((totalSeconds / 3600) % 24);
  const d = Math.floor(totalSeconds / 86400);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (d > 0) return `${d}z ${pad(h)}:${pad(m)}:${pad(s)}`;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

/** mm:ss dintr-un countdown scurt (OTP). */
export function formatMmSs(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds % 60));
  const m = Math.max(0, Math.floor(totalSeconds / 60));
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Etichetă lizibilă pentru cele 4 contoare de sold. */
export function counterLabel(counter: string): string {
  switch (counter) {
    case 'organic_premium':
      return 'Organic · Premium';
    case 'organic_ultra':
      return 'Organic · Ultra';
    case 'reclame_premium':
      return 'Reclame · Premium';
    case 'reclame_ultra':
      return 'Reclame · Ultra';
    default:
      return counter;
  }
}

export function categoryLabel(category: string): string {
  return category === 'reclame' ? 'Reclamă' : 'Organic';
}
export function tierLabel(tier: string): string {
  return tier === 'ultra' ? 'Ultra' : 'Premium';
}
